/**
 * Sentry Error Tracking & Performance Monitoring
 * EU Instance (Germany) - GDPR Compliant
 * 
 * Integrations:
 * - Browser Tracing: Performance monitoring for API calls and navigation
 * - Session Replay: 10% of sessions, 100% on errors
 * - User Context: Automatic user identification on auth
 * 
 * Privacy:
 * - beforeSend hook filters sensitive data (tokens, API keys)
 * - PII collection enabled for user context only
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE;

let sentryInitialized = false;

/**
 * Initialize Sentry error tracking
 * Safe to call multiple times - will only initialize once
 * Call this BEFORE React renders (in main.jsx)
 */
export function initSentry() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (sentryInitialized) {
    return;
  }

  // Check if DSN is set
  if (!SENTRY_DSN) {
    console.warn('[Sentry] Missing DSN - error tracking disabled');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      release: SENTRY_RELEASE,

      // Integrations
      integrations: [
        // Performance monitoring
        Sentry.browserTracingIntegration({
          // Track navigation timing
          tracePropagationTargets: [
            'localhost',
            /^https:\/\/fhgxapmrciwlhsffdeyj\.supabase\.co/,
            /^https:\/\/reedle-server-js\.onrender\.com/,
            /^https:\/\/reedle-server-py-eu\.onrender\.com/,
          ],
        }),

        // Session replay for debugging
        Sentry.replayIntegration({
          maskAllText: false, // Don't mask text (we filter sensitive data in beforeSend)
          blockAllMedia: false, // Don't block media
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev

      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of normal sessions
      replaysOnErrorSampleRate: 1.0, // 100% of error sessions

      // Send user context (email, ID)
      sendDefaultPii: true,

      // Privacy: Filter sensitive data before sending
      beforeSend(event, hint) {
        // Filter out sensitive data from request headers
        if (event.request?.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
          delete event.request.headers['apikey'];
        }

        // Filter sensitive data from breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
            if (breadcrumb.data?.headers) {
              delete breadcrumb.data.headers.Authorization;
              delete breadcrumb.data.headers.authorization;
              delete breadcrumb.data.headers['apikey'];
            }
            return breadcrumb;
          });
        }

        // Filter environment variables from context
        if (event.contexts?.runtime?.env) {
          const env = event.contexts.runtime.env;
          Object.keys(env).forEach(key => {
            if (key.includes('KEY') || key.includes('SECRET') || key.includes('TOKEN')) {
              delete env[key];
            }
          });
        }

        return event;
      },

      // Ignore common non-critical errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // Network errors (handled by app)
        'NetworkError',
        'Failed to fetch',
        // ResizeObserver (benign)
        'ResizeObserver loop limit exceeded',
      ],
    });

    sentryInitialized = true;
    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    // Can't use logger here since Sentry isn't initialized yet
    console.error('[Sentry] Initialization failed:', error);
  }
}

/**
 * Check if Sentry is initialized
 */
export function isSentryInitialized() {
  return sentryInitialized;
}

/**
 * Get Sentry instance for advanced usage
 */
export function getSentry() {
  return Sentry;
}

